import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Bell, Check, X, Filter, Eye, EyeOff } from "lucide-react";

const CATEGORY_LABELS = {
  all: "All", leads: "Leads", concepts: "Concepts", proposals: "Proposals",
  portal: "Portal", tasks: "Tasks", system: "System",
};

export default function NotificationBell() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [category, setCategory] = useState("all");
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef();

  const loadCount = () => api.get("/notifications/unread-count").then((r) => setUnread(r.data.unread)).catch(() => {});

  const load = useCallback((opts = {}) => {
    setLoading(true);
    const p = new URLSearchParams();
    if (category !== "all") p.set("category", category);
    if (onlyUnread) p.set("unread_only", "true");
    if (opts.before) p.set("before", opts.before);
    p.set("limit", "20");
    api.get(`/notifications?${p}`).then((r) => {
      setItems((prev) => opts.before ? [...prev, ...r.data.items] : r.data.items);
      setUnread(r.data.unread);
      setHasMore(!!r.data.has_more);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [category, onlyUnread]);

  useEffect(() => { loadCount(); const t = setInterval(loadCount, 45000); return () => clearInterval(t); }, []);
  useEffect(() => { if (open) load(); }, [open, load]);

  // Click outside to close
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const optimisticMark = (id, read) => {
    setItems((xs) => xs.map((n) => n.id === id ? { ...n, read } : n));
    setUnread((c) => Math.max(0, c + (read ? -1 : 1)));
  };

  const markRead = async (n, andOpen = false) => {
    if (!n.read) { optimisticMark(n.id, true); api.post(`/notifications/${n.id}/read`).catch(() => optimisticMark(n.id, false)); }
    if (andOpen && n.target_url) { setOpen(false); nav(n.target_url); }
  };
  const toggleRead = async (n) => {
    optimisticMark(n.id, !n.read);
    const path = n.read ? "unread" : "read";
    api.post(`/notifications/${n.id}/${path}`).catch(() => optimisticMark(n.id, n.read));
  };
  const markAll = async () => {
    setItems((xs) => xs.map((n) => ({ ...n, read: true }))); setUnread(0);
    await api.post("/notifications/read-all").catch(() => loadCount());
  };

  const badge = unread > 99 ? "99+" : unread > 9 ? "9+" : String(unread);

  return (
    <div className="relative" ref={wrapRef} data-testid="notif-bell">
      <button
        data-testid="btn-notif-bell"
        onClick={() => setOpen((v) => !v)}
        className="relative flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground w-full"
        aria-label="Notifications"
      >
        <Bell className="w-4 h-4" strokeWidth={1.5}/>
        <span>Notifications</span>
        {unread > 0 && (
          <span data-testid="notif-badge"
                className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full min-w-[18px] text-center"
                style={{ background: "#B85C38", color: "white" }}>{badge}</span>
        )}
      </button>

      {open && (
        <div
          data-testid="notif-drawer"
          className="fixed lg:absolute z-50 top-16 lg:top-auto lg:bottom-full left-4 right-4 lg:left-auto lg:right-0 lg:mb-2 lg:w-[380px] bg-card border border-border shadow-xl"
        >
          <div className="p-3 border-b border-border flex items-center justify-between">
            <div className="font-serif text-lg">Notifications</div>
            <div className="flex items-center gap-2">
              <button data-testid="btn-toggle-unread"
                onClick={() => setOnlyUnread((v) => !v)}
                className={`text-[10px] px-2 py-1 border ${onlyUnread ? "bg-foreground text-background border-foreground" : "border-input"}`}>
                {onlyUnread ? "Unread only" : "Show all"}
              </button>
              {unread > 0 && (
                <button data-testid="btn-notif-mark-all" onClick={markAll} className="text-[10px] px-2 py-1 border border-input">
                  <Check className="w-3 h-3 inline" strokeWidth={2}/> Mark all
                </button>
              )}
              <button onClick={() => setOpen(false)} className="p-1 text-muted-foreground"><X className="w-4 h-4" strokeWidth={1.5}/></button>
            </div>
          </div>

          <div className="px-3 py-2 border-b border-border overflow-x-auto flex gap-1" data-testid="notif-filters">
            {["all", "leads", "concepts", "proposals", "portal", "tasks", "system"].map((k) => (
              <button key={k}
                data-testid={`notif-filter-${k}`}
                onClick={() => setCategory(k)}
                className={`text-[10px] px-2 py-1 border whitespace-nowrap ${category === k ? "bg-foreground text-background border-foreground" : "border-input"}`}>
                {CATEGORY_LABELS[k]}
              </button>
            ))}
          </div>

          <div className="max-h-[60vh] overflow-y-auto" data-testid="notif-list">
            {items.length === 0 && !loading && (
              <div className="p-6 text-center text-xs text-muted-foreground">You're all caught up.</div>
            )}
            {items.map((n) => (
              <div key={n.id}
                className={`p-3 border-b border-border flex items-start gap-3 ${n.read ? "opacity-60" : ""} hover:bg-[color:hsl(var(--surface-panel))]`}
                data-testid={`notif-item-${n.id}`}>
                <div className="w-1 h-1 rounded-full mt-2" style={{ background: n.read ? "#8A8A8A" : "#B85C38" }}/>
                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => markRead(n, true)}>
                  <div className="text-sm font-medium">{n.title}</div>
                  {n.description && <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{n.description}</div>}
                  <div className="text-[10px] text-muted-foreground mt-1 flex items-center gap-2">
                    <span className="capitalize">{n.category || "system"}</span>
                    <span>·</span>
                    <span>{new Date(n.created_at).toLocaleString("en-AU")}</span>
                  </div>
                </div>
                <button data-testid={`btn-toggle-read-${n.id}`}
                  onClick={(e) => { e.stopPropagation(); toggleRead(n); }}
                  title={n.read ? "Mark unread" : "Mark read"}
                  className="p-1 text-muted-foreground hover:text-foreground">
                  {n.read ? <EyeOff className="w-3 h-3" strokeWidth={1.5}/> : <Eye className="w-3 h-3" strokeWidth={1.5}/>}
                </button>
              </div>
            ))}
            {hasMore && items.length > 0 && (
              <button data-testid="btn-notif-load-more"
                onClick={() => load({ before: items[items.length - 1].created_at })}
                className="w-full text-xs py-3 text-muted-foreground hover:text-foreground border-t border-border">
                {loading ? "Loading…" : "Load more"}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
